import { useParams } from "react-router-dom"
import { useCallback, useEffect, useRef, useState } from "react"
import {
    Alert,
    Box,
    Chip,
    Container,
    InputAdornment,
    Paper,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from "@mui/material"
import { RichTextEditor } from "../../app/RichTextEditor"
import HomeIcon from "@mui/icons-material/Home"
import BlurCircularIcon from "@mui/icons-material/BlurCircular"
import SyncAltIcon from "@mui/icons-material/SyncAlt"
import CompressIcon from "@mui/icons-material/Compress"
import ThermostatIcon from "@mui/icons-material/Thermostat"
import WbSunnyIcon from "@mui/icons-material/WbSunny"
import FilterAltIcon from "@mui/icons-material/FilterAlt"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import CancelIcon from "@mui/icons-material/Cancel"
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked"
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid"
import { enUS, plPL } from "@mui/x-data-grid/locales"
import { Localized, useLocalization } from "@fluent/react"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { selectMeasurementHeaderById, updateMeasurementHeader } from "./measurementHeadersSlice"
import { CleaningRoom, selectCleaningRoomById } from "./cleaningRoomsSlice"
import { selectSensorById } from "./sensorsSlice"
import { Measurement, MeasureLocation } from "./measurementsApi"
import { MeasurmentType } from "./types"
import { ISO_PARTICLE_LIMITS } from "./Utils/particleCalc"
import { selectSettings } from "../settings/settingsSlice"
import { AppId } from "../../app/appTypes"

const TYPE_ICONS: Record<MeasurmentType, React.ReactElement> = {
    'particles-in-air': <BlurCircularIcon />,
    'air-exchange-rate': <SyncAltIcon />,
    'pressure-difference': <CompressIcon />,
    'temp-and-humidity': <ThermostatIcon />,
    'light-intensity': <WbSunnyIcon />,
    'HEPA-integrity-and-tightness': <FilterAltIcon />,
}

const CIRCLE_RADIUS = 12

const applyTolerance = (loc: MeasureLocation, tolerance: number): MeasureLocation => {
    if (loc.referenceValue == null) return { ...loc, minValue: null, maxValue: null }
    const delta = loc.referenceValue * (tolerance / 100)
    return { ...loc, minValue: loc.referenceValue - delta, maxValue: loc.referenceValue + delta }
}

interface MeasurementPanelProps {
    activeMeasurement: Measurement
    measurementId: AppId
    room?: CleaningRoom
    onLocationsUpdate: (type: MeasurmentType, newLocations: MeasureLocation[]) => void
    onToleranceChange: (type: MeasurmentType, tolerance: number, newLocations: MeasureLocation[]) => void
    onMethodologyChange: (type: MeasurmentType, methodology: string) => void
}

const MeasurementPanel = ({ activeMeasurement, measurementId, room, onLocationsUpdate, onToleranceChange, onMethodologyChange }: MeasurementPanelProps) => {
    const { l10n } = useLocalization()
    const settings = useAppSelector(selectSettings)
    const locale = settings?.locale ?? 'en-US'

    const sensor = useAppSelector(state => selectSensorById(state, activeMeasurement.sensorId))

    const [pendingPlacement, setPendingPlacement] = useState<AppId | null>(null)
    const [cursor, setCursor] = useState<string>('default')

    const canvasRef = useRef<HTMLCanvasElement>(null)
    const bgImgRef = useRef<HTMLImageElement | null>(null)
    const draggingRef = useRef<{ locationId: AppId; offsetX: number; offsetY: number } | null>(null)

    const locations = activeMeasurement.locations
    const isParticles = activeMeasurement.type === 'particles-in-air'
    const showCanvas = isParticles
    const particleLimits = isParticles && room?.cleanlinessClass
        ? ISO_PARTICLE_LIMITS[room.cleanlinessClass] ?? null
        : null

    const dispatchUpdateLocations = useCallback((newLocations: MeasureLocation[]) => {
        onLocationsUpdate(activeMeasurement.type, newLocations)
    }, [onLocationsUpdate, activeMeasurement.type])

    const redraw = useCallback(() => {
        const canvas = canvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext('2d')
        if (!ctx) return

        const w = canvas.width
        const h = canvas.height

        ctx.clearRect(0, 0, w, h)

        if (bgImgRef.current) {
            ctx.drawImage(bgImgRef.current, 0, 0, w, h)
        } else {
            ctx.fillStyle = '#f5f5f5'
            ctx.fillRect(0, 0, w, h)
        }

        for (const loc of locations) {
            if (loc.x == null || loc.y == null) continue
            const px = (loc.x / 100) * w
            const py = (loc.y / 100) * h

            ctx.beginPath()
            ctx.arc(px, py, CIRCLE_RADIUS, 0, 2 * Math.PI)
            ctx.fillStyle = loc.id === pendingPlacement ? 'rgba(255,193,7,0.3)' : 'rgba(211,47,47,0.85)'
            ctx.fill()
            ctx.strokeStyle = loc.id === pendingPlacement ? '#ffc107' : '#b71c1c'
            ctx.lineWidth = 2
            ctx.stroke()

            ctx.fillStyle = '#fff'
            ctx.font = `bold 9px sans-serif`
            ctx.textAlign = 'center'
            ctx.textBaseline = 'middle'
            ctx.fillText(loc.code, px, py)
        }
    }, [locations, pendingPlacement])

    useEffect(() => {
        const drawing = activeMeasurement.roomDrawing
        if (drawing) {
            const img = new Image()
            img.onload = () => {
                bgImgRef.current = img
                redraw()
            }
            img.src = drawing
        } else {
            bgImgRef.current = null
            redraw()
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeMeasurement.roomDrawing])

    useEffect(() => {
        redraw()
    }, [redraw])

    const getCanvasPercent = (e: React.MouseEvent<HTMLCanvasElement>): { px: number; py: number } => {
        const canvas = canvasRef.current!
        const rect = canvas.getBoundingClientRect()
        const scaleX = canvas.width / rect.width
        const scaleY = canvas.height / rect.height
        const px = ((e.clientX - rect.left) * scaleX / canvas.width) * 100
        const py = ((e.clientY - rect.top) * scaleY / canvas.height) * 100
        return { px, py }
    }

    const getLocationAtPoint = (px: number, py: number): AppId | null => {
        const canvas = canvasRef.current
        if (!canvas) return null
        const w = canvas.width
        const h = canvas.height
        for (const loc of locations) {
            if (loc.x == null || loc.y == null) continue
            const cx = (loc.x / 100) * w
            const cy = (loc.y / 100) * h
            const ex = (px / 100) * w
            const ey = (py / 100) * h
            if (Math.sqrt((ex - cx) ** 2 + (ey - cy) ** 2) <= CIRCLE_RADIUS) {
                return loc.id
            }
        }
        return null
    }

    const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
        const { px, py } = getCanvasPercent(e)
        const hit = getLocationAtPoint(px, py)

        if (hit) {
            const loc = locations.find(l => l.id === hit)!
            const canvas = canvasRef.current!
            const w = canvas.width
            const h = canvas.height
            const ex = (px / 100) * w
            const ey = (py / 100) * h
            const cx = (loc.x! / 100) * w
            const cy = (loc.y! / 100) * h
            draggingRef.current = { locationId: hit, offsetX: ex - cx, offsetY: ey - cy }
            setCursor('grabbing')
        } else if (pendingPlacement) {
            const newLocations = locations.map(l =>
                l.id === pendingPlacement ? { ...l, x: px, y: py } : l
            )
            dispatchUpdateLocations(newLocations)
            setPendingPlacement(null)
            setCursor('default')
        }
    }

    const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (draggingRef.current) {
            const { px, py } = getCanvasPercent(e)
            const canvas = canvasRef.current!
            const w = canvas.width
            const h = canvas.height
            const ex = (px / 100) * w
            const ey = (py / 100) * h
            const nx = ((ex - draggingRef.current.offsetX) / w) * 100
            const ny = ((ey - draggingRef.current.offsetY) / h) * 100
            const newLocations = locations.map(l =>
                l.id === draggingRef.current!.locationId
                    ? { ...l, x: Math.max(0, Math.min(100, nx)), y: Math.max(0, Math.min(100, ny)) }
                    : l
            )
            dispatchUpdateLocations(newLocations)
            return
        }

        const { px, py } = getCanvasPercent(e)
        const hit = getLocationAtPoint(px, py)
        if (hit) {
            setCursor('grab')
        } else if (pendingPlacement) {
            setCursor('crosshair')
        } else {
            setCursor('default')
        }
    }

    const handleMouseUp = () => {
        if (draggingRef.current) {
            draggingRef.current = null
            setCursor('default')
        }
    }

    const processRowUpdate = (newRow: MeasureLocation) => {
        const updated = isParticles
            ? newRow
            : applyTolerance(newRow, activeMeasurement.tolerance)
        const newLocations = locations.map(l => l.id === updated.id ? updated : l)
        dispatchUpdateLocations(newLocations)
        return updated
    }

    const dataGridLocale = locale === 'pl' ? plPL.components.MuiDataGrid.defaultProps.localeText : enUS.components.MuiDataGrid.defaultProps.localeText

    const placedColumn: GridColDef<MeasureLocation> = {
        field: 'placed',
        headerName: l10n.getString('measurement-location-placed', null, 'Placed'),
        width: 80,
        renderCell: (params: GridRenderCellParams<MeasureLocation>) =>
            params.row.x != null
                ? <CheckCircleIcon color="success" fontSize="small" />
                : <RadioButtonUncheckedIcon color="disabled" fontSize="small" />,
    }

    const locationsWithData05 = locations.filter(l => l.concentration05um != null)
    const locationsWithData5  = locations.filter(l => l.concentration5um  != null)
    const avg05um = locationsWithData05.length
        ? Math.round(locationsWithData05.reduce((s, l) => s + l.concentration05um!, 0) / locationsWithData05.length)
        : null
    const avg5um = locationsWithData5.length
        ? Math.round(locationsWithData5.reduce((s, l) => s + l.concentration5um!, 0) / locationsWithData5.length)
        : null

    const makeResultCell = (value: number | null, limit: number | null) => {
        if (value == null || limit == null) return <Chip label="—" size="small" />
        return value <= limit
            ? <Chip icon={<CheckCircleIcon />} label={l10n.getString('measurement-pass', null, 'Pass')} color="success" size="small" />
            : <Chip icon={<CancelIcon />} label={l10n.getString('measurement-fail', null, 'Fail')} color="error" size="small" />
    }

    const particleColumns: GridColDef<MeasureLocation>[] = [
        {
            field: 'code',
            headerName: l10n.getString('measurement-location-code', null, 'Code'),
            width: 80,
        },
        {
            field: 'remarks',
            headerName: l10n.getString('measurement-location-remarks', null, 'Remarks'),
            flex: 1,
            editable: true,
        },
        {
            field: 'concentration05um',
            headerName: l10n.getString('measurement-concentration-05um', null, '≥0.5 µm (p/m³)'),
            width: 155,
            editable: true,
            type: 'number',
        },
        {
            field: '_avg05um',
            headerName: l10n.getString('measurement-avg-05um', null, 'Avg ≥0.5 µm (p/m³)'),
            width: 160,
            type: 'number',
            valueGetter: () => avg05um,
        },
        {
            field: 'concentration5um',
            headerName: l10n.getString('measurement-concentration-5um', null, '≥5.0 µm (p/m³)'),
            width: 155,
            editable: true,
            type: 'number',
        },
        {
            field: '_avg5um',
            headerName: l10n.getString('measurement-avg-5um', null, 'Avg ≥5.0 µm (p/m³)'),
            width: 160,
            type: 'number',
            valueGetter: () => avg5um,
        },
        {
            field: '_result05um',
            headerName: l10n.getString('measurement-particle-result-05um', null, '0.5 µm result'),
            width: 130,
            renderCell: (params: GridRenderCellParams<MeasureLocation>) =>
                makeResultCell(params.row.concentration05um, particleLimits?.max05um ?? null),
        },
        {
            field: '_result5um',
            headerName: l10n.getString('measurement-particle-result-5um', null, '5.0 µm result'),
            width: 130,
            renderCell: (params: GridRenderCellParams<MeasureLocation>) =>
                makeResultCell(params.row.concentration5um, particleLimits?.max5um ?? null),
        },
        placedColumn,
    ]

    const genericColumns: GridColDef<MeasureLocation>[] = [
        {
            field: 'code',
            headerName: l10n.getString('measurement-location-code', null, 'Code'),
            width: 80,
        },
        {
            field: 'remarks',
            headerName: l10n.getString('measurement-location-remarks', null, 'Remarks'),
            flex: 1,
            editable: true,
        },
        {
            field: 'measuredValue',
            headerName: l10n.getString('measurement-location-measured-value', null, 'Measured value'),
            width: 120,
            editable: true,
            type: 'number',
        },
        {
            field: 'referenceValue',
            headerName: l10n.getString('measurement-location-reference-value', null, 'Reference value'),
            width: 130,
            editable: true,
            type: 'number',
        },
        {
            field: 'minValue',
            headerName: l10n.getString('measurement-location-min-value', null, 'Min'),
            width: 90,
            type: 'number',
        },
        {
            field: 'maxValue',
            headerName: l10n.getString('measurement-location-max-value', null, 'Max'),
            width: 90,
            type: 'number',
        },
        {
            field: 'unit',
            headerName: l10n.getString('measurement-location-unit', null, 'Unit'),
            width: 100,
            editable: true,
        },
    ]

    const columns: GridColDef<MeasureLocation>[] = isParticles ? particleColumns : genericColumns

    const typeIcon = TYPE_ICONS[activeMeasurement.type]
    const typeLabel = l10n.getString(`measurment-type-${activeMeasurement.type}`, null, activeMeasurement.type)

    return (
        <>
            {/* Type header */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', fontSize: '2rem' }}>
                    {typeIcon}
                </Box>
                <Typography variant="h5">{typeLabel}</Typography>
                <Chip label={activeMeasurement.date} size="small" />
            </Stack>

            {/* Particle limits summary */}
            {isParticles && room && (
                <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
                    <Stack direction="row" spacing={3} flexWrap="wrap" alignItems="center">
                        {room.cleanlinessClass && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    <Localized id="cleaning-room-cleanliness-class">Cleanliness class</Localized>
                                </Typography>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {room.cleanlinessClass === 'ISO 1-9' ? 'ISO 1-9' : `GMP Class ${room.cleanlinessClass}`}
                                </Typography>
                            </Box>
                        )}
                        {room.maxParticles05um != null && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    <Localized id="measurement-limit-05um">Limit ≥0.5 µm (p/m³)</Localized>
                                </Typography>
                                <Typography variant="subtitle1">{room.maxParticles05um.toLocaleString()}</Typography>
                            </Box>
                        )}
                        {room.maxParticles5um != null && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    <Localized id="measurement-limit-5um">Limit ≥5.0 µm (p/m³)</Localized>
                                </Typography>
                                <Typography variant="subtitle1">{room.maxParticles5um.toLocaleString()}</Typography>
                            </Box>
                        )}
                    </Stack>
                </Paper>
            )}

            {/* Methodology */}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                <Localized id="measurement-methodology">Measurement</Localized>
            </Typography>
            <Box sx={{ mb: 3, width: '100%' }}>
                <RichTextEditor
                    value={activeMeasurement.methodology}
                    onValueChange={val => onMethodologyChange(activeMeasurement.type, val)}
                    label={<></>}
                />
            </Box>

            {/* Sensor info card */}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                <Localized id="measurement-sensor">Sensor</Localized>
            </Typography>
            {sensor && (
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Typography variant="subtitle1">{sensor.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{sensor.producer}</Typography>
                    <Typography variant="body2">
                        <Localized id="sensor-calibration-date">Calibration date</Localized>
                        {': '}{sensor.calibrationDate ?? '—'}
                    </Typography>
                </Paper>
            )}

            {/* Canvas */}
            {showCanvas && <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                <Localized id="measurement-room-drawing">Rzut pomieszczenia</Localized>
            </Typography>}
            {showCanvas && (
                <Box
                    sx={{
                        position: 'relative',
                        mb: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        overflow: 'hidden',
                    }}
                >
                    {pendingPlacement && (
                        <Chip
                            label={l10n.getString('measurement-click-to-place', null, 'Click on the canvas to place the location')}
                            color="warning"
                            size="small"
                            sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}
                        />
                    )}
                    <canvas
                        ref={canvasRef}
                        width={800}
                        height={400}
                        style={{
                            height: 400,
                            display: 'block',
                            cursor,
                        }}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                    />
                </Box>
            )}

            {/* Tolerance — not applicable for particle measurements (limits are fixed by ISO class) */}
            {!isParticles && (
                <TextField
                    label={<Localized id="measurement-tolerance"><span>Tolerance</span></Localized>}
                    type="number"
                    size="small"
                    value={activeMeasurement.tolerance}
                    onChange={e => {
                        const tolerance = Number(e.target.value)
                        const newLocations = locations.map(l => applyTolerance(l, tolerance))
                        onToleranceChange(activeMeasurement.type, tolerance, newLocations)
                    }}
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                    sx={{ mb: 2, width: 160 }}
                />
            )}

            {/* Locations DataGrid */}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                <Localized id="measurement-result-table-caption">Measurement result</Localized>
            </Typography>
            <DataGrid
                rows={locations}
                columns={columns}
                processRowUpdate={processRowUpdate}
                onRowClick={showCanvas ? (params) => {
                    const loc = params.row as MeasureLocation
                    setPendingPlacement(prev => prev === loc.id ? null : loc.id)
                    setCursor(pendingPlacement === loc.id ? 'default' : 'crosshair')
                } : undefined}
                getRowClassName={(params) =>
                    (params.row as MeasureLocation).id === pendingPlacement ? 'place-location' : ''
                }
                localeText={dataGridLocale}
                sx={{
                    '& .place-location': { bgcolor: 'action.selected' },
                }}
                autoHeight
                disableRowSelectionOnClick
                hideFooter
            />
        </>
    )
}

export const MeasurementForm = () => {
    const { id: paramId } = useParams()
    const id = paramId ?? ''
    const { l10n } = useLocalization()
    const dispatch = useAppDispatch()

    const measurement = useAppSelector(state => selectMeasurementHeaderById(state, id))
    const room = useAppSelector(state =>
        measurement ? selectCleaningRoomById(state, measurement.clearRoomId) : undefined
    )

    const [activeTab, setActiveTab] = useState(0)

    const measurementEntries = Object.values(measurement?.measurements ?? {}).filter(Boolean) as Measurement[]

    const dispatchUpdateLocations = useCallback((type: MeasurmentType, newLocations: MeasureLocation[]) => {
        if (!measurement) return
        dispatch(updateMeasurementHeader({
            id: measurement.id,
            changes: {
                measurements: {
                    ...measurement.measurements,
                    [type]: { ...measurement.measurements[type]!, locations: newLocations },
                },
            },
        }))
    }, [dispatch, measurement])

    const dispatchToleranceChange = useCallback((type: MeasurmentType, tolerance: number, newLocations: MeasureLocation[]) => {
        if (!measurement) return
        dispatch(updateMeasurementHeader({
            id: measurement.id,
            changes: {
                measurements: {
                    ...measurement.measurements,
                    [type]: { ...measurement.measurements[type]!, tolerance, locations: newLocations },
                },
            },
        }))
    }, [dispatch, measurement])

    const dispatchMethodologyChange = useCallback((type: MeasurmentType, methodology: string) => {
        if (!measurement) return
        dispatch(updateMeasurementHeader({
            id: measurement.id,
            changes: {
                measurements: {
                    ...measurement.measurements,
                    [type]: { ...measurement.measurements[type]!, methodology },
                },
            },
        }))
    }, [dispatch, measurement])

    if (!measurement) {
        return (
            <Container maxWidth={false} sx={{ py: 3 }}>
                <Alert severity="warning">
                    <Localized id="error-entity-not-found">Entity not found</Localized>
                </Alert>
            </Container>
        )
    }

    const safeTab = Math.min(activeTab, measurementEntries.length - 1)
    const activeMeasurement = measurementEntries[safeTab]

    return (
        <Container maxWidth={false} sx={{ py: 3 }}>
            <Stack spacing={3}>
                {/* Section 1: Clean Room Panel */}
                <Paper elevation={2} sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                        <HomeIcon color="primary" />
                        <Typography variant="h6">
                            {room ? `${room.number} – ${room.name}` : measurement.clearRoomId}
                        </Typography>
                    </Stack>
                    {room && (
                        <Stack direction="row" spacing={3} flexWrap="wrap">
                            <Stack direction="row" spacing={2} alignItems="center">
                                {room.area != null && (
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="h6" color="primary.main" lineHeight={1}>
                                            {room.area}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            <Localized id="cleaning-room-area">Area (m²)</Localized>
                                        </Typography>
                                    </Box>
                                )}
                                {room.area != null && room.volume != null && (
                                    <Box sx={{ width: '1px', height: 36, bgcolor: 'divider' }} />
                                )}
                                {room.volume != null && (
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="h6" color="primary.main" lineHeight={1}>
                                            {room.volume}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            <Localized id="cleaning-room-volume">Volume (m³)</Localized>
                                        </Typography>
                                    </Box>
                                )}
                            </Stack>
                            {[room.address1, room.address2, room.city, room.state, room.zipcode, room.country].some(Boolean) && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                                    <HomeIcon fontSize="small" sx={{ opacity: 0.5, flexShrink: 0 }} />
                                    <Typography variant="body2">
                                        {[
                                            [room.address1, room.address2].filter(Boolean).join(', '),
                                            [room.city, room.state, room.zipcode].filter(Boolean).join(' '),
                                            room.country,
                                        ].filter(Boolean).join(' · ')}
                                    </Typography>
                                </Box>
                            )}
                        </Stack>
                    )}
                </Paper>

                {/* Section 2: Measurement Panel */}
                <Paper elevation={2} sx={{ p: 3 }}>
                    {measurementEntries.length > 1 && (
                        <Tabs
                            value={safeTab}
                            onChange={(_, val) => setActiveTab(val)}
                            sx={{ mb: 2 }}
                        >
                            {measurementEntries.map((m, i) => (
                                <Tab
                                    key={m.type}
                                    icon={TYPE_ICONS[m.type]}
                                    label={l10n.getString(`measurment-type-${m.type}`, null, m.type)}
                                    iconPosition="start"
                                />
                            ))}
                        </Tabs>
                    )}
                    {activeMeasurement && (
                        <MeasurementPanel
                            key={activeMeasurement.type}
                            activeMeasurement={activeMeasurement}
                            measurementId={measurement.id}
                            room={room}
                            onLocationsUpdate={dispatchUpdateLocations}
                            onToleranceChange={dispatchToleranceChange}
                            onMethodologyChange={dispatchMethodologyChange}
                        />
                    )}
                </Paper>
            </Stack>
        </Container>
    )
}
